// src/components/Properties/ShareButton.jsx
// ======================================================
// 🔥 SHARE BUTTON ULTRA PREMIUM — NIVEL AIRBNB/ZILLOW
// ======================================================
// V2: ANTI-BUCLES — Usa /p/imovel para SSR preview en WhatsApp
// 3 variantes en 1 componente:
// - floating: botón flotante con pulso (detalle, listado)
// - icon: icono discreto para cada PropertyCard
// - pill: botón "Compartilhar vitrine" en header
// ======================================================

import React, { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Share2, MessageCircle } from 'lucide-react';
import ShareModal from './ShareModal';

// ======================================================
// 🔧 UTILIDADES (V2: /p/imovel para SSR)
// ======================================================

const getPublicPropertyUrl = (id) => {
  const base = window.location.origin;
  return `${base}/p/imovel/${id}`;
};

const getVitrineUrl = (companyId) => {
  const base = window.location.origin;
  return `${base}/imoveis?companyId=${companyId}`;
};

// ======================================================
// 🎨 SHARE BUTTON COMPONENT
// ======================================================

export default function ShareButton({
  // Props del botón
  variant = 'icon',        // 'floating' | 'icon' | 'pill'
  
  // Datos a compartir
  property = null,          // Propiedad individual (si es null, comparte vitrina)
  companyId = null,         // Para compartir vitrina completa
  primaryColor = '#2563eb',
  
  // Configuración visual
  size = 'md',              // 'sm' | 'md' | 'lg'
  label = 'Compartilhar',
  showLabel = false,
  
  // Classes adicionales
  className = '',
  
  // Callbacks
  onShare = null,
  onOpen = null,
  onClose = null,
}) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isPressed, setIsPressed] = useState(false);

  // Determinar URL a compartir
  const shareUrl = property?.id
    ? getPublicPropertyUrl(property.id)
    : getVitrineUrl(companyId);

  const shareTitle = property
    ? property.title || 'Imóvel'
    : 'Vitrine de Imóveis';

  const shareProperty = property || {
    title: shareTitle,
    price: null,
    address: null,
    bedrooms: null,
    area: null,
  };

  // Abrir modal
  const handleOpen = useCallback(() => {
    setIsModalOpen(true);
    onOpen?.();
  }, [onOpen]);

  // Cerrar modal
  const handleClose = useCallback(() => {
    setIsModalOpen(false);
    onClose?.();
  }, [onClose]);

  // Compartir directo por WhatsApp (sin modal)
  const handleWhatsAppDirect = useCallback(() => {
    const message = property
      ? `🏡 *${property.title || 'Imóvel'}*\n💰 ${property.price ? `R$ ${Number(property.price).toLocaleString('pt-BR')}` : 'Valor sob consulta'}\n📍 ${property.address || ''}\n\n🔗 ${shareUrl}`
      : `🏡 *Vitrine de Imóveis*\n\nConfira os imóveis disponíveis:\n🔗 ${shareUrl}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, '_blank');
    onShare?.('whatsapp');
  }, [property, shareUrl, onShare]);

  // Tamaños
  const sizeClasses = {
    sm: { button: 'w-8 h-8', icon: 'w-4 h-4' },
    md: { button: 'w-10 h-10', icon: 'w-5 h-5' },
    lg: { button: 'w-14 h-14', icon: 'w-6 h-6' },
  };

  const currentSize = sizeClasses[size] || sizeClasses.md;

  // ======================================================
  // VARIANTE: FLOATING ACTION BUTTON
  // ======================================================
  if (variant === 'floating') {
    return (
      <>
        <motion.button
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: 'spring', stiffness: 400, delay: 0.3 }}
          whileHover={{ scale: 1.08 }}
          whileTap={{ scale: 0.92 }}
          onHoverStart={() => setIsHovered(true)}
          onHoverEnd={() => setIsHovered(false)}
          onMouseDown={() => setIsPressed(true)}
          onMouseUp={() => setIsPressed(false)}
          onClick={handleWhatsAppDirect}
          onContextMenu={(e) => {
            e.preventDefault();
            handleOpen();
          }}
          className={`fixed bottom-6 right-6 z-50 rounded-full shadow-2xl flex items-center justify-center transition-all group ${className}`}
          style={{
            backgroundColor: primaryColor,
            width: '60px',
            height: '60px',
            boxShadow: `0 8px 30px ${primaryColor}40`,
          }}
          title="Compartilhar no WhatsApp (clique direito para mais opções)"
          aria-label="Compartilhar imóvel"
        >
          <MessageCircle className="w-7 h-7 text-white" />
          
          {/* Pulso animado */}
          <span className="absolute inset-0 rounded-full animate-ping opacity-20" style={{ backgroundColor: primaryColor }} />
          
          {/* Indicador de más opciones */}
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-white rounded-full flex items-center justify-center shadow-md">
            <Share2 className="w-3 h-3" style={{ color: primaryColor }} />
          </span>
          
          {/* Tooltip en hover */}
          <AnimatePresence>
            {isHovered && (
              <motion.div
                initial={{ opacity: 0, x: 10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 10 }}
                className="absolute right-full mr-3 px-3 py-2 bg-slate-800 text-white text-xs rounded-lg whitespace-nowrap shadow-lg pointer-events-none"
              >
                Compartilhar
                <div className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-full">
                  <div className="w-2 h-2 bg-slate-800 rotate-45" />
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.button>

        {/* Modal de compartir (clic derecho) */}
        <ShareModal
          isOpen={isModalOpen}
          onClose={handleClose}
          property={shareProperty}
          url={shareUrl}
          primaryColor={primaryColor}
          title={property ? 'Compartilhar Imóvel' : 'Compartilhar Vitrine'}
        />
      </>
    );
  }

  // ======================================================
  // VARIANTE: ICON BUTTON (PARA CARDS)
  // ======================================================
  if (variant === 'icon') {
    return (
      <>
        <motion.button
          whileHover={{ scale: 1.15 }}
          whileTap={{ scale: 0.85 }}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            handleOpen();
          }}
          className={`rounded-full flex items-center justify-center transition-all ${className}`}
          style={{
            width: currentSize.button,
            height: currentSize.button,
            backgroundColor: isHovered ? `${primaryColor}15` : 'transparent',
          }}
          onHoverStart={() => setIsHovered(true)}
          onHoverEnd={() => setIsHovered(false)}
          title="Compartilhar imóvel"
          aria-label="Compartilhar imóvel"
        >
          <Share2
            className={currentSize.icon}
            style={{ color: isHovered ? primaryColor : '#94a3b8' }}
          />
        </motion.button>

        <ShareModal
          isOpen={isModalOpen}
          onClose={handleClose}
          property={shareProperty}
          url={shareUrl}
          primaryColor={primaryColor}
          title="Compartilhar Imóvel"
        />
      </>
    );
  }

  // ======================================================
  // VARIANTE: PILL BUTTON (PARA HEADER)
  // ======================================================
  if (variant === 'pill') {
    return (
      <>
        <motion.button
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.97 }}
          onClick={handleOpen}
          className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium text-sm transition-all shadow-md hover:shadow-lg ${className}`}
          style={{
            backgroundColor: primaryColor,
            color: 'white',
            boxShadow: `0 4px 15px ${primaryColor}30`,
          }}
        >
          <Share2 className="w-4 h-4" />
          {label && <span>{label}</span>}
          {showLabel && (
            <span className="hidden md:inline text-white/80 text-xs ml-1">
              • {property ? 'Imóvel' : 'Vitrine'}
            </span>
          )}
        </motion.button>

        <ShareModal
          isOpen={isModalOpen}
          onClose={handleClose}
          property={shareProperty}
          url={shareUrl}
          primaryColor={primaryColor}
          title={property ? 'Compartilhar Imóvel' : 'Compartilhar Vitrine'}
        />
      </>
    );
  }

  // Fallback
  return null;
}

// ======================================================
// EXPORTACIONES NOMBRADAS PARA USO DIRECTO
// ======================================================

export const FloatingShareButton = (props) => (
  <ShareButton variant="floating" {...props} />
);

export const IconShareButton = (props) => (
  <ShareButton variant="icon" {...props} />
);

export const PillShareButton = (props) => (
  <ShareButton variant="pill" {...props} />
);